import React from 'react';
import { Trophy, Award, Star } from 'lucide-react';

interface Notification {
  type: 'achievement' | 'rank_up' | 'points';
  title?: string;
  rank?: string;
  points?: number;
  image?: string;
  message: string;
  timestamp: number;
  id: string;
}

interface GamificationNotificationProps {
  notification: Notification;
  isOpen: boolean;
  onClose: () => void;
  duration?: number;
}

export default function GamificationNotification({ 
  notification, 
  isOpen, 
  onClose, 
  duration = 5000 
}: GamificationNotificationProps) {
  React.useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      onClose && onClose();
    }, duration);
    return () => clearTimeout(t);
  }, [isOpen, duration, onClose]);

  const getIcon = () => {
    switch(notification.type) {
      case 'achievement':
        return <Award size={32} color="var(--orange)" />;
      case 'rank_up':
        return <Trophy size={32} color="var(--orange)" />;
      case 'points':
        return <Star size={32} color="var(--orange)" />;
      default:
        return <Award size={32} color="var(--orange)" />;
    }
  };

  const getTitle = () => {
    switch(notification.type) {
      case 'achievement':
        return '🏆 Achievement Unlocked!';
      case 'rank_up':
        return '⭐ Rank Up!';
      case 'points':
        return '✨ XP Earned!';
      default:
        return '🎉 Notification';
    }
  };

  return (
    <div
      style={{ pointerEvents: 'none', zIndex: 10000 }}
      className="fixed top-6 right-0 w-full max-w-md px-4"
    >
      <div
        className={`ml-auto transform transition-all duration-500 ${
          isOpen ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'
        }`}
      >
        <div
          className="shadow-2xl border-2 rounded-lg overflow-hidden relative"
          style={{
            background: 'linear-gradient(135deg, var(--raisin-black-1) 0%, #1a1a2e 100%)',
            borderColor: 'var(--orange)',
            color: 'var(--white)',
            pointerEvents: 'auto',
            boxShadow: '0 20px 60px rgba(20, 254, 251, 0.3), 0 0 0 1px var(--orange)',
          }}
        >
          {/* Animated background effect */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at top right, rgba(20, 254, 251, 0.15) 0%, transparent 70%)',
            animation: 'pulse 2s ease-in-out infinite',
          }} />
          
          <div className="p-5 flex items-start gap-4 relative z-10">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ 
                  background: 'rgba(20, 254, 251, 0.2)',
                  border: '2px solid var(--orange)',
                  animation: 'bounce 1s ease-in-out'
                }}
              >
                {notification.image ? (
                  <img 
                    src={notification.image} 
                    alt="" 
                    style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }} 
                  />
                ) : (
                  getIcon()
                )}
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: 'var(--orange)' }}>
                {getTitle()}
              </p>
              <p className="text-lg font-bold leading-tight mb-1" style={{ color: 'var(--white)' }}>
                {notification.title || notification.rank || `+${notification.points} XP`}
              </p>
              {notification.points && notification.type === 'achievement' && (
                <p className="text-sm" style={{ color: 'var(--orange)', marginBottom: '4px' }}>
                  +{notification.points} XP
                </p>
              )}
              <p className="text-sm" style={{ color: 'var(--platinum)' }}>
                {notification.message}
              </p>
            </div>
            
            {/* Close button */}
            <button
              onClick={onClose}
              className="px-2 py-1 text-sm rounded hover:bg-orange-500/20 transition-colors flex-shrink-0"
              style={{ 
                color: 'var(--orange)',
                fontSize: '24px',
                fontWeight: 'bold',
                minWidth: '32px',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
        </div>
      </div>
      
      {/* Add animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  );
}

